import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  BusinessOutlined as SupplierIcon,
  CalendarTodayOutlined as CreatedIcon,
  ContactPhoneOutlined as ContactsIcon,
  EditOutlined as EditIcon,
  EmailOutlined as EmailIcon,
  EventAvailableOutlined as SinceIcon,
  FlagOutlined as StatusIcon,
  Inventory2Outlined as MaterialsIcon,
  ListAltOutlined as TableIcon,
  LocationOnOutlined as LocationIcon,
  MapOutlined as MapIcon,
  NotesOutlined as NotesIcon,
  PersonOutline as PersonIcon,
  PhoneOutlined as PhoneIcon,
  SearchOffOutlined as NotFoundIcon,
} from '@mui/icons-material';
import CollectionPointFormDialog from '../../components/collection-points/CollectionPointFormDialog';
import LoadingSkeleton from '../../components/LoadingSkeleton';
import PageHeader from '../../components/PageHeader';
import StatusChip from '../../components/StatusChip';
import SupplierAgreementPanel from '../../components/suppliers/SupplierAgreementPanel';
import SupplierCollectionPoints from '../../components/suppliers/SupplierCollectionPoints';
import SupplierFormDialog from '../../components/suppliers/SupplierFormDialog';
import FlowBuilder from '../../components/collection-flow/FlowBuilder';
import { APP_SHAPE } from '../../constants/shape';
import { useAuthPermissions } from '../../hooks/useAuthPermissions';
import { useConfirm } from '../../hooks/useConfirm';
import { syncOutletCollectionSites } from '../../services/supplier.service';
import { getAllWasteCatalogMaterials, type WasteCatalogOption } from '../../services/waste-catalog.service';
import { useCollectionPointStore } from '../../stores/collection-point.store';
import { useOrganizationStore } from '../../stores/organization.store';
import { useSupplierStore } from '../../stores/supplier.store';
import type { SupplierCollectionPoint, SupplierCollectionPointInput, SupplierInput } from '../../types/supplier.type';

type CollectionPointView = 'map' | 'table';
type DetailSection = 'points' | 'agreement' | 'flow';

function SidebarDetail({ icon, label, children }: { icon: ReactNode; label: string; children: ReactNode }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '20px minmax(0, 1fr)', gap: 1.5 }}>
      <Box sx={{ color: 'text.secondary', display: 'flex', mt: 0.25, '& svg': { fontSize: 20 } }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
        <Box sx={{ mt: 0.5 }}>{children}</Box>
      </Box>
    </Box>
  );
}

function MetricCard({ icon, label, value }: { icon: ReactNode; label: string; value: ReactNode }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: `${APP_SHAPE.radiusLg}px`, height: '100%' }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box sx={{ color: 'primary.main', display: 'flex', '& svg': { fontSize: 26 } }}>{icon}</Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}>{value}</Typography>
          <Typography variant="caption" color="text.secondary">{label}</Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(new Date(value));
}

export default function SupplierDetails() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { supplierId } = useParams<{ supplierId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedOrganization = useOrganizationStore((state) => state.selectedOrganization);
  const {
    selectedSupplier,
    selectedSupplierNotFound,
    isLoading,
    isSaving,
    error,
    clearError,
    fetchSupplier,
    updateSupplier,
  } = useSupplierStore();
  const {
    isSaving: isSavingPoint,
    error: pointError,
    clearError: clearPointError,
    createCollectionPoint,
    updateCollectionPoint,
    archiveCollectionPoint,
  } = useCollectionPointStore();
  const { canMutate } = useAuthPermissions();
  const { confirm, confirmDialog } = useConfirm();
  const [editOpen, setEditOpen] = useState(false);
  const [pointFormOpen, setPointFormOpen] = useState(false);
  const [editingPoint, setEditingPoint] = useState<SupplierCollectionPoint | null>(null);
  const [materialOptions, setMaterialOptions] = useState<WasteCatalogOption[]>([]);

  const orgId = selectedOrganization?.id;
  const canEdit = Boolean(orgId && canMutate(`recyclee.${orgId}.*.supplier.write`));
  const canArchive = Boolean(orgId && canMutate(`recyclee.${orgId}.*.supplier.delete`));
  const view: CollectionPointView = searchParams.get('view') === 'table' ? 'table' : 'map';
  const tabParam = searchParams.get('tab');
  // A delivering partner (another recycler / waste mover that offloads into one of our
  // linked facilities) has no collection points and no collection flow: only agreement
  // prices apply to what it delivers.
  const isDeliveringPartner = selectedSupplier?.source === 'facility_linked';
  const section: DetailSection = isDeliveringPartner
    ? 'agreement'
    : tabParam === 'agreement' || tabParam === 'flow'
      ? tabParam
      : 'points';

  const setSearchParam = (key: string, value: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set(key, value);
        return next;
      },
      { replace: true },
    );
  };

  useEffect(() => {
    if (orgId && supplierId) void fetchSupplier(orgId, supplierId);
  }, [orgId, supplierId, fetchSupplier]);

  useEffect(() => {
    // Unscoped on purpose: these are labels for existing records, which may
    // reference a waste type this organization no longer handles.
    void getAllWasteCatalogMaterials().then(setMaterialOptions).catch(() => setMaterialOptions([]));
  }, []);

  const materialLabels = useMemo(
    () => new Map(materialOptions.map((option) => [option.value, option.label])),
    [materialOptions],
  );

  if (!selectedOrganization) return <Alert severity="info">{t('suppliers.details.selectOrganization')}</Alert>;

  if (isLoading && (!selectedSupplier || selectedSupplier.id !== supplierId)) {
    return <LoadingSkeleton variant="cards" filters={false} cardCount={3} />;
  }

  if (selectedSupplierNotFound) {
    return (
      <Box>
        <PageHeader title={t('suppliers.details.pageTitle')} onBack={() => navigate('/suppliers')} backLabel={t('suppliers.details.backLabel')} sx={{ borderRadius: `${APP_SHAPE.radiusLg}px` }} />
        <Paper variant="outlined" sx={{ p: { xs: 3, sm: 6 }, textAlign: 'center', borderRadius: `${APP_SHAPE.radiusLg}px` }}>
          <NotFoundIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1.5 }} />
          <Typography variant="h5">{t('suppliers.details.notFoundTitle')}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 3 }}>
            {t('suppliers.details.notFoundHint')}
          </Typography>
          <Button variant="outlined" onClick={() => navigate('/suppliers')}>{t('suppliers.details.backLabel')}</Button>
        </Paper>
      </Box>
    );
  }

  if (!selectedSupplier || selectedSupplier.id !== supplierId) {
    return (
      <Box>
        <PageHeader title={t('suppliers.details.pageTitle')} onBack={() => navigate('/suppliers')} backLabel={t('suppliers.details.backLabel')} sx={{ borderRadius: `${APP_SHAPE.radiusLg}px` }} />
        <Alert severity="error" action={<Button color="inherit" onClick={() => supplierId && void fetchSupplier(selectedOrganization.id, supplierId)}>{t('suppliers.details.retry')}</Button>}>
          {error || t('suppliers.details.loadError')}
        </Alert>
      </Box>
    );
  }

  const supplier = selectedSupplier;
  const collectionPoints = supplier.collection_points ?? [];
  const activeCollectionPoints = collectionPoints.filter((point) => point.status !== 'archived').length;
  const save = async (input: SupplierInput) => Boolean(await updateSupplier(selectedOrganization.id, supplier.id, input));

  const refreshSupplier = () => void fetchSupplier(selectedOrganization.id, supplier.id);

  const [syncingSites, setSyncingSites] = useState(false);
  const [syncNotice, setSyncNotice] = useState<string | null>(null);
  const syncFromOutlet = async () => {
    setSyncingSites(true);
    setSyncNotice(null);
    try {
      const result = await syncOutletCollectionSites(selectedOrganization.id, supplier.id);
      setSyncNotice(
        t('suppliers.collectionPoints.syncResult', {
          created: result.created.length,
          skipped: result.skipped.length,
        })
      );
      refreshSupplier();
    } catch (syncError) {
      const detail = (syncError as any)?.response?.data?.detail;
      setSyncNotice(typeof detail === 'string' ? detail : t('suppliers.collectionPoints.syncFailed'));
    } finally {
      setSyncingSites(false);
    }
  };

  const openAddPoint = () => {
    clearPointError();
    setEditingPoint(null);
    setPointFormOpen(true);
  };

  const openEditPoint = (point: SupplierCollectionPoint) => {
    clearPointError();
    setEditingPoint(point);
    setPointFormOpen(true);
  };

  const savePoint = async (input: SupplierCollectionPointInput) => {
    let result;
    if (editingPoint) {
      const { supplier_id, ...updateInput } = input;
      result = await updateCollectionPoint(selectedOrganization.id, editingPoint.id, updateInput);
    } else {
      result = await createCollectionPoint(selectedOrganization.id, input);
    }
    if (result) refreshSupplier();
    return Boolean(result);
  };

  const archivePoint = async (point: SupplierCollectionPoint) => {
    const confirmed = await confirm({
      title: t('common.archive'),
      description: t('suppliers.collectionPoints.archiveConfirm', { name: point.name }),
      tone: 'danger',
    });
    if (!confirmed) return;
    const success = await archiveCollectionPoint(selectedOrganization.id, point.id);
    if (success) refreshSupplier();
  };

  return (
    <Box>
      <PageHeader
        icon={<SupplierIcon />}
        title={supplier.name}
        subtitle={isDeliveringPartner ? t('suppliers.source.facilityLinkedHint') : t('suppliers.details.subtitle')}
        onBack={() => navigate('/suppliers')}
        backLabel={t('suppliers.details.backLabel')}
        sx={{ borderRadius: `${APP_SHAPE.radiusLg}px` }}
        actions={(
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ width: { xs: '100%', sm: 'auto' } }}>
            {canEdit && <Button variant="contained" startIcon={<EditIcon />} onClick={() => setEditOpen(true)}>{t('suppliers.details.editSupplier')}</Button>}
          </Stack>
        )}
      />

      {error && !editOpen && <Alert severity="error" onClose={clearError} sx={{ mb: 3 }}>{error}</Alert>}

      <Grid container spacing={2} sx={{ mb: { xs: 2, sm: 3 } }}>
        <Grid item xs={6} md={3}>
          <MetricCard icon={<LocationIcon />} label={t('suppliers.details.metrics.collectionPoints')} value={supplier.collection_points_count} />
        </Grid>
        <Grid item xs={6} md={3}>
          <MetricCard icon={<MapIcon />} label={t('suppliers.details.metrics.activeCollectionPoints')} value={activeCollectionPoints} />
        </Grid>
        <Grid item xs={6} md={3}>
          <MetricCard icon={<MaterialsIcon />} label={t('suppliers.details.metrics.wasteTypes')} value={supplier.material_ids.length} />
        </Grid>
        <Grid item xs={6} md={3}>
          <MetricCard icon={<SinceIcon />} label={t('suppliers.details.metrics.supplierSince')} value={formatDate(supplier.created_at)} />
        </Grid>
      </Grid>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: '320px minmax(0, 1fr)' },
          gap: { xs: 2, sm: 3 },
          alignItems: 'start',
        }}
      >
        <Paper
          component="aside"
          aria-label={t('suppliers.details.sidebarTitle')}
          elevation={1}
          sx={{ borderRadius: `${APP_SHAPE.radiusLg}px`, overflow: 'hidden' }}
        >
          <Box sx={{ p: 3 }}>
            <Typography variant="h6">{t('suppliers.details.sidebarTitle')}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t('suppliers.details.sidebarSubtitle')}
            </Typography>
          </Box>
          <Divider />
          <Stack spacing={3} sx={{ p: 3 }} divider={<Divider flexItem />}>
            <SidebarDetail icon={<StatusIcon />} label={t('suppliers.details.fields.status')}>
              <StatusChip value={supplier.status} />
            </SidebarDetail>

            <SidebarDetail icon={<LocationIcon />} label={t('suppliers.details.fields.collectionPoints')}>
              <Typography variant="body2" fontWeight={600} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {supplier.collection_points_count}
              </Typography>
            </SidebarDetail>

            <SidebarDetail icon={<MaterialsIcon />} label={t('suppliers.details.fields.wasteTypes')}>
              <Stack direction="row" gap={0.75} useFlexGap flexWrap="wrap">
                {supplier.material_ids.map((id) => (
                  <Chip
                    key={id}
                    size="small"
                    label={materialLabels.get(id) ?? id}
                    sx={{ borderRadius: `${APP_SHAPE.radiusSm}px`, maxWidth: '100%' }}
                  />
                ))}
                {!supplier.material_ids.length && <Typography variant="body2">{t('suppliers.details.fields.notSpecified')}</Typography>}
              </Stack>
            </SidebarDetail>

            <SidebarDetail icon={<NotesIcon />} label={t('suppliers.details.fields.notes')}>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                {supplier.internal_notes || t('suppliers.details.fields.noNotes')}
              </Typography>
            </SidebarDetail>

            <SidebarDetail icon={<ContactsIcon />} label={t('suppliers.details.fields.contacts')}>
              <Stack spacing={2}>
                {supplier.contacts.map((contact, index) => (
                  <Box key={`${contact.name}-${index}`}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <PersonIcon fontSize="small" color="action" />
                      <Typography variant="body2" fontWeight={600}>{contact.name}</Typography>
                    </Stack>
                    {contact.role && <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>{contact.role}</Typography>}
                    {contact.phone && (
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                        <PhoneIcon sx={{ fontSize: 16 }} color="action" />
                        <Typography variant="body2" dir="ltr" sx={{ overflowWrap: 'anywhere' }}>{contact.phone}</Typography>
                      </Stack>
                    )}
                    {contact.email && (
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                        <EmailIcon sx={{ fontSize: 16 }} color="action" />
                        <Typography variant="body2" dir="ltr" sx={{ overflowWrap: 'anywhere' }}>{contact.email}</Typography>
                      </Stack>
                    )}
                  </Box>
                ))}
                {!supplier.contacts.length && <Typography variant="body2">{t('suppliers.details.fields.noContacts')}</Typography>}
              </Stack>
            </SidebarDetail>

            <SidebarDetail icon={<CreatedIcon />} label={t('suppliers.details.fields.created')}>
              <Typography variant="body2">{formatDate(supplier.created_at)}</Typography>
            </SidebarDetail>
          </Stack>
        </Paper>

        <Paper elevation={1} sx={{ minWidth: 0, borderRadius: `${APP_SHAPE.radiusLg}px`, overflow: 'hidden' }}>
          <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2 }}>
            <Tabs
              value={section}
              onChange={(_, nextSection: DetailSection) => setSearchParam('tab', nextSection)}
              aria-label={t('suppliers.details.sidebarTitle')}
            >
              {!isDeliveringPartner ? (
                <Tab value="points" label={t('suppliers.details.tabs.collectionPoints')} id="supplier-detail-points-tab" aria-controls="supplier-detail-points-panel" />
              ) : null}
              <Tab value="agreement" label={t('suppliers.details.tabs.agreement')} id="supplier-detail-agreement-tab" aria-controls="supplier-detail-agreement-panel" />
              {!isDeliveringPartner ? (
                <Tab value="flow" label={t('collectionFlow.supplierTitle')} id="supplier-detail-flow-tab" aria-controls="supplier-detail-flow-panel" />
              ) : null}
            </Tabs>
          </Box>
          <Divider />

          {section === 'points' ? (
            <Box role="tabpanel" id="supplier-detail-points-panel" aria-labelledby="supplier-detail-points-tab">
              <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                <Tabs
                  value={view}
                  onChange={(_, nextView: CollectionPointView) => setSearchParam('view', nextView)}
                  variant="scrollable"
                  scrollButtons="auto"
                  aria-label={t('suppliers.details.collectionPointViewsLabel')}
                >
                  <Tab value="map" icon={<MapIcon fontSize="small" />} iconPosition="start" label={t('suppliers.view.map')} id="collection-points-map-tab" aria-controls="collection-points-map-panel" />
                  <Tab value="table" icon={<TableIcon fontSize="small" />} iconPosition="start" label={t('suppliers.view.table')} id="collection-points-table-tab" aria-controls="collection-points-table-panel" />
                </Tabs>
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  {canEdit && supplier.supplier_org_id && (
                    <Button size="small" variant="outlined" onClick={() => void syncFromOutlet()} disabled={syncingSites}>
                      {syncingSites
                        ? t('suppliers.collectionPoints.syncing')
                        : t('suppliers.collectionPoints.syncFromOutlet')}
                    </Button>
                  )}
                  {canEdit && (
                    <Button size="small" startIcon={<AddIcon />} onClick={openAddPoint}>
                      {t('suppliers.collectionPoints.list.addPoint')}
                    </Button>
                  )}
                </Box>
              </Box>
              <Divider />
              {pointError && <Alert severity="error" onClose={clearPointError} sx={{ m: 2 }}>{pointError}</Alert>}
              {syncNotice && <Alert severity="info" onClose={() => setSyncNotice(null)} sx={{ m: 2 }}>{syncNotice}</Alert>}
              <Box
                role="tabpanel"
                id={`collection-points-${view}-panel`}
                aria-labelledby={`collection-points-${view}-tab`}
              >
                <SupplierCollectionPoints
                  points={collectionPoints}
                  materialLabels={materialLabels}
                  view={view}
                  canMutate={canEdit || canArchive}
                  onEdit={canEdit ? openEditPoint : undefined}
                  onArchive={canArchive ? (point) => void archivePoint(point) : undefined}
                />
              </Box>
            </Box>
          ) : null}

          {section === 'agreement' ? (
            <Box role="tabpanel" id="supplier-detail-agreement-panel" aria-labelledby="supplier-detail-agreement-tab">
              <SupplierAgreementPanel
                organizationId={selectedOrganization.id}
                supplierId={supplier.id}
                materialIds={supplier.material_ids}
                materialLabels={materialLabels}
                canEdit={canEdit}
              />
            </Box>
          ) : null}

          {section === 'flow' ? (
            <Box
              role="tabpanel"
              id="supplier-detail-flow-panel"
              aria-labelledby="supplier-detail-flow-tab"
              sx={{ p: { xs: 2, sm: 3 } }}
            >
              {/* How a driver completes a pickup at this supplier's sites. */}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('collectionFlow.supplierSubtitle')}
              </Typography>
              <FlowBuilder
                organizationId={selectedOrganization.id}
                flowType="supplier_collection"
                supplierId={supplier.id}
              />
            </Box>
          ) : null}
        </Paper>
      </Box>

      <SupplierFormDialog
        open={editOpen}
        supplier={supplier}
        saving={isSaving}
        submitError={editOpen ? error : null}
        onClose={() => setEditOpen(false)}
        onSubmit={save}
      />

      <CollectionPointFormDialog
        open={pointFormOpen}
        organizationId={selectedOrganization.id}
        supplierId={supplier.id}
        point={editingPoint}
        saving={isSavingPoint}
        submitError={pointFormOpen ? pointError : null}
        onClose={() => setPointFormOpen(false)}
        onSubmit={savePoint}
      />
      {confirmDialog}
    </Box>
  );
}
